import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import YouTube from "react-youtube";
import useVipMultiviewSoundStore from "../../../store/useVipMultiviewSoundStore";

// FORCE LIVE: seek to an absolute position far beyond any possible live
// head — the player clamps to its true live edge. We deliberately do NOT
// compute this from getDuration(): on these embeds dur is often reported
// as (currentPosition + 3600), so a dur-relative overshoot only hops you
// forward ~2h per press instead of reaching live. An absolute huge target
// is immune to that. ~4 months in seconds; YouTube live DVR windows and
// stream ages can never reach it.
const LIVE_SEEK_TARGET_SEC = 9999999;

const YouTubePlayer = forwardRef(
  ({ videoId, onEnded, onError, isSelected }, ref) => {
    const { isMuted, volume } = useVipMultiviewSoundStore();
    const playerRef = useRef(null);
    const [isPlayerReady, setIsPlayerReady] = useState(false);
    const currentVideoId = useRef(videoId);

    const opts = {
      height: "100%",
      width: "100%",
      playerVars: {
        // https://developers.google.com/youtube/player_parameters
        autoplay: 1,
        mute: 1, // Start muted by default, we'll update it after player loads
        controls: 0,
        modestbranding: 1,
        rel: 0,
        playsinline: 1,
        enablejsapi: 1,
        cc_load_policy: 0,
      },
    };

    // Manual FORCE LIVE — parent calls this via ref on the selected tile.
    // Absolute overshoot: player clamps to its true live edge on the FIRST
    // press regardless of what getDuration() claims. If the tile was
    // already live it's a harmless ~1-2s blip; if it was stale it jumps to
    // live. internalPlayer methods are promise-based.
    // NOTE: on a NON-live video this seeks to the end and fires onEnd —
    // only call it on live broadcasts (which multiview is).
    useImperativeHandle(ref, () => ({
      jumpToLive: async () => {
        const p = playerRef.current?.internalPlayer;
        if (!p) return;
        try {
          console.log("[force-live] seeking to absolute live-edge clamp");
          await p.seekTo(LIVE_SEEK_TARGET_SEC, true);
          await p.playVideo();
        } catch (e) {}
      },
    }));

    // Track when videoId changes to reset ready state
    useEffect(() => {
      // If video ID changed, mark player as not ready
      if (currentVideoId.current !== videoId) {
        // console.log("Video ID changed, resetting player ready state");
        setIsPlayerReady(false);
        currentVideoId.current = videoId;
      }
    }, [videoId]);

    // Update player volume and mute state whenever selection, volume or global mute changes
    // BUT only if the player is fully ready for the current video
    useEffect(() => {
      // console.log("Effect was triggered, isPlayerReady:", isPlayerReady);
      if (
        playerRef.current &&
        playerRef.current.internalPlayer &&
        isPlayerReady
      ) {
        // console.log("Player is ready");
        const player = playerRef.current.internalPlayer;

        // Set volume (YouTube API uses 0-100)
        if (isSelected) {
          player.setVolume(volume);
        }

        // Only play audio if this player is selected AND global mute is off
        if (isSelected && !isMuted) {
          player.unMute();
        } else {
          player.mute();
        }
      }
    }, [isSelected, isMuted, volume, isPlayerReady]);

    const handleReady = (event) => {
      // console.log("Player ready: ", event);
      // When player is ready, set the initial mute state based on selection

      const player = event.target;
      // Kill auto-captions. YouTube began defaulting live-stream captions ON
      // (server-side rollout, mid-2026). There's no official "off" param, so
      // we unload the caption modules on ready — the long-standing standard
      // workaround. Both module names covered (player build varies).
      try {
        player.unloadModule("captions");
        player.unloadModule("cc");
      } catch (e) {}

      // Set initial volume
      if (isSelected) {
        player.setVolume(volume);
      }

      // Only unmute if this is the selected player AND global mute is off
      if (isSelected && !isMuted) {
        player.unMute();
      } else {
        player.mute();
      }

      // Mark player as ready for this specific video
      setIsPlayerReady(true);
    };

    return (
      <div
        style={{
          position: "relative",
          height: "100%",
          width: "100%",
        }}
      >
        <YouTube
          ref={playerRef}
          videoId={videoId}
          opts={opts}
          style={{
            width: "100%",
            height: "100%",
          }}
          onEnd={() => {
            console.warn("VIDEO ENDED");
            if (onEnded) onEnded(videoId);
          }}
          onError={(error) => {
            console.log("Error: ", error);
            if (onError) onError(error, videoId);
          }}
          onPlay={handleReady}
        />
      </div>
    );
  }
);

export default YouTubePlayer;
